'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface PaymentMethod {
  id: string;
  type: 'card' | 'upi';
  name: string;
  details: string;
  isDefault: boolean;
}

interface PaymentMethodsSectionProps {
  paymentMethods: PaymentMethod[];
}

const PaymentMethodsSection = ({ paymentMethods: initialMethods }: PaymentMethodsSectionProps) => {
  const [methods, setMethods] = useState<PaymentMethod[]>(initialMethods);
  const [isAddingNew, setIsAddingNew] = useState(false);

  const handleSetDefault = (id: string) => {
    setMethods(
      methods.map((method) => ({
        ...method,
        isDefault: method.id === id,
      }))
    );
  };

  const handleDelete = (id: string) => {
    setMethods(methods.filter((method) => method.id !== id));
  };

  const getPaymentIcon = (type: string) => {
    return type === 'card' ? 'CreditCardIcon' : 'DevicePhoneMobileIcon';
  };

  return (
    <div className="bg-card rounded-lg border border-border p-6 shadow-elevation-sm">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 bg-primary/10 rounded-lg">
            <Icon name="CreditCardIcon" size={24} className="text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-lg text-foreground">Payment Methods</h2>
            <p className="text-sm text-muted-foreground">Manage your saved payment options</p>
          </div>
        </div>
        <button
          onClick={() => setIsAddingNew(true)}
          className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
        >
          <Icon name="PlusIcon" size={16} />
          <span className="hidden sm:inline">Add Method</span>
        </button>
      </div>

      <div className="space-y-3">
        {methods.map((method) => (
          <div
            key={method.id}
            className={`p-4 rounded-lg border transition-smooth ${
              method.isDefault
                ? 'border-primary bg-primary/5' :'border-border bg-background hover:border-muted-foreground'
            }`}
          >
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-center justify-center w-10 h-10 bg-card rounded-md">
                  <Icon name={getPaymentIcon(method.type) as any} size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-medium text-sm text-foreground">{method.name}</h3>
                    {method.isDefault && (
                      <span className="px-2 py-0.5 bg-primary text-primary-foreground text-xs font-medium rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground font-data">{method.details}</p>
                </div>
              </div>

              <div className="flex items-center gap-2">
                {!method.isDefault && (
                  <button
                    onClick={() => handleSetDefault(method.id)}
                    className="p-2 rounded-md hover:bg-muted transition-smooth"
                    aria-label="Set as default"
                  >
                    <Icon name="CheckCircleIcon" size={18} className="text-muted-foreground" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(method.id)}
                  className="p-2 rounded-md hover:bg-error/10 transition-smooth"
                  aria-label="Delete payment method"
                >
                  <Icon name="TrashIcon" size={18} className="text-error" />
                </button>
              </div>
            </div>
          </div>
        ))}

        {methods.length === 0 && (
          <div className="text-center py-12">
            <Icon name="CreditCardIcon" size={48} className="mx-auto text-muted-foreground mb-4" />
            <p className="text-sm text-muted-foreground mb-4">No payment methods saved</p>
            <button
              onClick={() => setIsAddingNew(true)}
              className="px-6 py-2 bg-primary text-primary-foreground rounded-md font-medium text-sm transition-smooth hover:shadow-elevation-md hover-lift"
            >
              Add Payment Method
            </button>
          </div>
        )}
      </div>

      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-lg">
          <Icon name="ShieldCheckIcon" size={20} className="text-success mt-0.5" />
          <div>
            <h4 className="font-medium text-sm text-foreground mb-1">Secure Payments</h4>
            <p className="text-xs text-muted-foreground">
              All payment information is encrypted and stored securely. We never share your financial details.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethodsSection;